import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '../../components/ui'
import { HelpTip } from '../../components/ui/HelpTip'
import { errorMessage } from '../../lib/api'
import { formatLeaveDays } from '../../lib/leaveDays'
import { useApproverBalance } from '../../lib/time'
import { balanceFigures } from './approverBalance'

/** Read-only. On a day someone reviews without a stage on this person's leave, the server refuses and nothing shows. */
export function LeaveBalanceStrip({ employeeId }: { employeeId: string }) {
  const { t, i18n } = useTranslation()
  const query = useApproverBalance(employeeId)
  if (query.isPending) return <LoadingState rows={1} />
  if (query.isError) {
    if (axios.isAxiosError(query.error) && query.error.response?.status === 403) return null
    return (
      <p role="alert" className="text-sm text-red-700 dark:text-red-300">
        {t('leave.remaining.loadFailed')} {errorMessage(query.error)}
      </p>
    )
  }
  const days = (value: number) => formatLeaveDays(value, i18n.language)
  return (
    <section data-testid="leave-balance-strip" className="space-y-1 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40">
      <h3 className="flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('leave.remaining.title')}
        <HelpTip label={t('leave.remaining.title')}>{t('leave.remaining.help')}</HelpTip>
      </h3>
      <dl className="flex flex-wrap gap-2">
        {balanceFigures(query.data).map((figure) => (
          <div
            key={figure.code}
            title={figure.name}
            className={`min-w-16 rounded-md px-2 py-1 ${
              figure.overdrawn
                ? 'bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100'
                : 'bg-surface text-slate-800 dark:text-slate-100'
            }`}
          >
            <dt className="text-xs font-medium">{figure.code}</dt>
            <dd className="text-sm font-semibold tabular-nums">{days(figure.remaining)}</dd>
            {figure.pending > 0 && (
              <dd className="text-xs tabular-nums">{t('leave.remaining.pending', { days: days(figure.pending) })}</dd>
            )}
          </div>
        ))}
        <div className="min-w-16 rounded-md bg-surface px-2 py-1 text-slate-800 dark:text-slate-100">
          <dt className="text-xs font-medium">{t('leave.remaining.takenSl')}</dt>
          <dd className="text-sm font-semibold tabular-nums">{days(Number(query.data.taken_sl_days))}</dd>
        </div>
      </dl>
    </section>
  )
}
